/**
 * PATH: packages/email-hub/src/feature/reader-header/ReaderHeaderFrame.tsx
 * FILE_ID: EMAILHUB-READERHEADER-FRAME-001
 * PURPOSE: Assemble the command, identity, triage, workflow, and notice bands.
 * ROLE IN ARCHITECTURE: Shared recursive frame used by preview and full readers.
 * MAIN EXPORTS: ReaderHeaderFrame, ReaderHeaderFrameProps.
 * NON-RESPONSIBILITIES: Host adapters own navigation, mutations, overlays, and body rendering.
 * NOTES FOR FUTURE AI: Keep this frame to hooks, guards, and JSX; logic belongs in selectors/leaves.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { cn } from '../../_internal/lib/utils'
import type { Email } from '../hooks/useEmails'
import {
  ReaderCommandBar,
  type ReaderCommandAction,
} from './command/CommandBar'
import { ReaderIdentityBand } from './identity/IdentityBand'
import { buildReaderHeaderViewModel } from './model/buildReaderHeader'
import type {
  ReaderHeaderSurface,
  ReaderNotice,
  ReaderNoticeKind,
} from './model/types'
import { selectLeadAtRestNotice, sortReaderNotices } from './notices/noticeRegistry'
import { ReaderNoticeSummary } from './notices/NoticeSummary'
import {
  useReaderHeaderDensity,
  type ReaderHeaderDensity,
} from './responsive/density'
import { MOTION_MS } from './tokens'
import { ReaderTriageBand } from './triage/TriageBand'

export interface ReaderHeaderFrameProps {
  email: Email
  surface: ReaderHeaderSurface
  shellOrgId?: number | null
  /**
   * Membership count for the cross-organisation decision gate. The frame
   * rebuilds its own view model, so it must receive this alongside shellOrgId
   * or it will disagree with the adapter about which decisions exist.
   */
  memberOrgCount?: number | null
  narrow: boolean
  positionLabel?: string
  onBack: () => void
  backLabel: string
  previous?: ReaderCommandAction
  next?: ReaderCommandAction
  reply: ReaderCommandAction
  replyAll?: ReaderCommandAction
  forward?: ReaderCommandAction
  done?: ReaderCommandAction
  overflowActions: readonly ReaderCommandAction[]
  onFind: () => void
  onExpand?: () => void
  /**
   * Related-context chips for the identity band (Synced from / Other emails).
   * Host-owned because only the host knows whether its context rail gave those
   * sections up — see ReaderIdentityBandProps.related.
   */
  identityRelated?: ReactNode
  /** Node, or a render function receiving the measured density tier. */
  decisionContent?: ReactNode | ((density: ReaderHeaderDensity) => ReactNode)
  summaryContent?: ReactNode
  /** Node, or a render function receiving the measured density tier. */
  expandedContext: ReactNode | ((density: ReaderHeaderDensity) => ReactNode)
  /**
   * Per-kind expanded body for a notice (change #269 Notice Center
   * unification) — e.g. `{ provider: <ProviderWritebackControls .../> }`.
   * Rendered ONLY while the operator has the Notice Center panel open, and
   * only for kinds that currently have a matching entry in `notices`; never
   * always-on like the old `noticeContent` prop it replaces. Omit entirely
   * to keep the frame's collapsed cost byte-identical to a header with no
   * notices at all.
   */
  noticeDetails?: Partial<Record<ReaderNoticeKind, ReactNode>>
  notices?: readonly ReaderNotice[]
  /** Locally resolved decision ids (optimistic queue advance, adapter-owned). */
  resolvedDecisionIds?: readonly string[]
}

export function ReaderHeaderFrame({
  email,
  surface,
  shellOrgId,
  memberOrgCount,
  narrow,
  positionLabel,
  onBack,
  backLabel,
  previous,
  next,
  reply,
  replyAll,
  forward,
  done,
  overflowActions,
  onFind,
  onExpand,
  identityRelated,
  decisionContent,
  summaryContent,
  expandedContext,
  noticeDetails,
  notices = [],
  resolvedDecisionIds,
}: ReaderHeaderFrameProps) {
  const [headerRef, density, densityMeasured] = useReaderHeaderDensity()
  const model = useMemo(
    () =>
      buildReaderHeaderViewModel({
        email,
        surface,
        shellOrgId,
        memberOrgCount,
        notices,
        resolvedDecisionIds,
      }),
    [email, memberOrgCount, notices, resolvedDecisionIds, shellOrgId, surface],
  )

  // WORK ITEM 11: ONE sr-only live region, wired ONLY to decision-queue-
  // advance and notice-appear/clear. Accept/reject/failure/undo already
  // announce via the toast's role="status" in tenant-shared — duplicating
  // them here would make screen readers say everything twice. Each effect
  // REPLACES liveMessage (never appends), so a burst of fast resolutions
  // never queues stale text.
  const [liveMessage, setLiveMessage] = useState('')
  const prevDecisionId = useRef<string | null>(null)
  useEffect(() => {
    const current = model.currentDecision
    const currentId = current?.id ?? null
    if (currentId === prevDecisionId.current) return
    if (current) {
      const remaining = model.decisions.length
      setLiveMessage(
        remaining > 1
          ? `Next decision: ${current.label} — ${remaining} pending.`
          : `Next decision: ${current.label}.`,
      )
    } else if (prevDecisionId.current !== null) {
      setLiveMessage('All decisions resolved.')
    }
    prevDecisionId.current = currentId
  }, [model.currentDecision, model.decisions.length])

  const prevNoticeId = useRef<string | null>(null)
  useEffect(() => {
    const lead = selectLeadAtRestNotice(model.notices)
    const leadId = lead?.id ?? null
    if (leadId === prevNoticeId.current) return
    if (lead) {
      setLiveMessage(lead.label)
    } else if (prevNoticeId.current !== null) {
      setLiveMessage('Notice cleared.')
    }
    prevNoticeId.current = leadId
  }, [model.notices])

  // Notice Center expansion (change #269): the ONLY bodies ever mounted are
  // the ones whose kind currently has BOTH a live notice AND a caller-
  // supplied `noticeDetails` entry — a kind with no detail node (calendar,
  // error today) still shows in the summary line but has nothing to expand.
  const expandableNotices = useMemo(
    () =>
      sortReaderNotices(model.notices).filter(
        (notice) => noticeDetails?.[notice.kind] != null,
      ),
    [model.notices, noticeDetails],
  )
  const [noticesExpanded, setNoticesExpanded] = useState(false)
  const toggleNotices = () => setNoticesExpanded((current) => !current)

  // Same expand/collapse idiom as TriageBand's expanded context panel
  // (WORK ITEM 9 precedent): grid-rows 0fr/1fr + MOTION_MS, with the
  // expensive subtree (real ProviderWritebackControls/PingNotificationCard)
  // delay-unmounted for one collapse duration so the shrink has something to
  // animate, then fully removed — the collapsed cost stays O(1) regardless
  // of how many notices exist.
  const [noticePanelMounted, setNoticePanelMounted] = useState(false)
  const [noticePanelOpen, setNoticePanelOpen] = useState(false)
  const noticeCollapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (noticesExpanded) {
      if (noticeCollapseTimer.current) {
        clearTimeout(noticeCollapseTimer.current)
        noticeCollapseTimer.current = null
      }
      setNoticePanelMounted(true)
      const raf = requestAnimationFrame(() => setNoticePanelOpen(true))
      return () => cancelAnimationFrame(raf)
    }
    setNoticePanelOpen(false)
    noticeCollapseTimer.current = setTimeout(() => {
      setNoticePanelMounted(false)
    }, MOTION_MS.collapse)
    return () => {
      if (noticeCollapseTimer.current) clearTimeout(noticeCollapseTimer.current)
    }
  }, [noticesExpanded])

  const compact = density === 'compact'
  const noticePanel = (
    <div
      className={cn(
        'border-t border-border/50 bg-background',
        compact
          ? 'fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-y-auto rounded-t-2xl border shadow-2xl'
          : 'relative',
      )}
      data-testid="reader-notice-details"
    >
      {compact && (
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-3 py-2">
          <span className="text-sm font-semibold">Notices</span>
          <button
            type="button"
            onClick={() => setNoticesExpanded(false)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11"
            aria-label="Close notices"
          >
            ×
          </button>
        </div>
      )}
      <div className="grid gap-2 p-2">
        {expandableNotices.map((notice) => (
          <div key={notice.kind} data-notice-detail-kind={notice.kind}>
            {noticeDetails?.[notice.kind]}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <header
      ref={headerRef}
      className="shrink-0 bg-background"
      data-reader-header-density={density}
      data-reader-header-surface={surface}
      data-testid="reader-header-frame"
    >
      <div role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </div>
      <ReaderCommandBar
        density={density}
        subject={model.subject}
        narrow={narrow}
        positionLabel={positionLabel}
        onBack={onBack}
        backLabel={backLabel}
        previous={previous}
        next={next}
        reply={reply}
        replyAll={replyAll}
        forward={forward}
        done={done}
        overflowActions={overflowActions}
        onFind={onFind}
        onExpand={onExpand}
      />
      <ReaderIdentityBand email={email} related={identityRelated} />
      <ReaderTriageBand
        model={model}
        density={density}
        densityMeasured={densityMeasured}
        decisionContent={
          typeof decisionContent === 'function'
            ? decisionContent(density)
            : decisionContent
        }
        summaryContent={summaryContent}
        expandedContent={
          typeof expandedContext === 'function'
            ? expandedContext(density)
            : expandedContext
        }
      />
      <ReaderNoticeSummary
        notices={model.notices}
        onOpen={expandableNotices.length > 0 ? toggleNotices : undefined}
      />
      {compact && noticesExpanded && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/35"
          aria-label="Close notices"
          onClick={() => setNoticesExpanded(false)}
        />
      )}
      {noticeDetails && (
        <div
          className={cn(
            'grid overflow-hidden transition-[grid-template-rows] ease-out motion-reduce:transition-none',
            noticePanelOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
          style={{
            transitionDuration: `${noticesExpanded ? MOTION_MS.expand : MOTION_MS.collapse}ms`,
          }}
        >
          <div className="min-h-0 overflow-hidden">
            {noticePanelMounted && noticePanel}
          </div>
        </div>
      )}
    </header>
  )
}
